import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
} from 'src/utils/ErrorDirectory';
import { resolveSendErrorAlert } from 'src/utils/sendErrorMessage';
import { useACLContext } from '../../../../../../providers/ACLProvider/ACL.hook';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context';
import { ChatEnum } from '../../../../Chat.enum';
import { useChatContext } from '../../../../Chat.hook';
import { withHumanAction } from 'src/components/Chat/Chat.humanAction';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { ChatService } from '../../../../Chat.service';
import {
  tInteractiveOutbound,
  tWapiInteractiveErrorBody,
} from '../../../../Chat.types';
import { useMessageBarContext } from '../../MessageBar.hook';
import { InteractiveDraftCache } from './InteractiveAction.cache';
import {
  buildInitialInteractiveValues,
  toMetaWireInteractive,
} from './InteractiveAction.functions';

// 500 ms captures natural typing pauses without spamming localStorage on
// every keystroke. Combined with the unmount-snapshot below this ensures
// the latest value is always persisted — even if the seller closes the
// composer mid-edit.
const DRAFT_SAVE_DEBOUNCE_MS = 500;

// Keeps Formik's `body` field synced with the main chat text input.
// First mount: if Formik has a preloaded body (from quick reply select or
// cache restore), push it to the text editor so both stay in sync.
// Subsequent updates: normal editor → Formik sync as the seller types.
export function useInteractiveBodySync(): void {
  const { messageBarValue, setMessageBarValue } = useMessageBarContext();
  const formik = useFormikContext<tInteractiveOutbound>();
  // Tracks whether the initial mount sync has chosen a direction. Without
  // this, the sync effect would fire on mount and wipe a preloaded body
  // by overwriting it with the empty messageBarValue.
  const hasSeededFromFormikRef = useRef(false);

  useEffect(() => {
    if (!hasSeededFromFormikRef.current) {
      hasSeededFromFormikRef.current = true;
      if (formik.values.body && formik.values.body !== messageBarValue) {
        setMessageBarValue(formik.values.body);
        return;
      }
    }
    if (formik.values.body !== messageBarValue) {
      formik.setFieldValue('body', messageBarValue);
      formik.setFieldTouched('body', true, false);
    }
    // Intentionally only re-run when `messageBarValue` changes — the
    // Formik reference (`formik`) is stable per render but including it
    // would re-run this effect on every keystroke in any other field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageBarValue]);
}

// Publishes the submit handler + isValid + isSending from the Formik
// child subtree into MessageBarContext's generic adapter slot so the
// shared SendAction paper-airplane can drive any composer. Clears the
// slot on unmount so a stale adapter can't be invoked after the composer
// closes.
export function useInteractiveSubmitPublisher(isSending: boolean): void {
  const { setComposerSubmitAdapter } = useMessageBarContext();
  const formik = useFormikContext<tInteractiveOutbound>();

  useEffect(() => {
    setComposerSubmitAdapter({
      submit: formik.submitForm,
      isValid: formik.isValid,
      isSending,
    });
  }, [formik.submitForm, formik.isValid, isSending, setComposerSubmitAdapter]);

  useEffect(() => {
    return () => setComposerSubmitAdapter(null);
  }, [setComposerSubmitAdapter]);
}

// Debounced persistence of the Formik draft to localStorage so the seller
// can close the composer / switch types and pick up where they left off.
// Unmount always flushes the latest snapshot — critical for the "X by
// mistake" case.
export function useInteractiveDraftPersistence(
  authId: string,
  workspaceId: string,
  chatId: string,
): void {
  const formik = useFormikContext<tInteractiveOutbound>();
  // Hold the latest values in a ref so the unmount cleanup can flush the
  // final snapshot without adding `formik.values` to its deps (which
  // would fire the cleanup on every keystroke).
  const latestValuesRef = useRef(formik.values);
  latestValuesRef.current = formik.values;

  useEffect(() => {
    if (!authId || !workspaceId || !chatId) return undefined;
    const timeoutId = window.setTimeout(() => {
      InteractiveDraftCache.save(authId, workspaceId, chatId, formik.values);
    }, DRAFT_SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [authId, chatId, workspaceId, formik.values]);

  useEffect(() => {
    return () => {
      if (authId && workspaceId && chatId) {
        InteractiveDraftCache.save(
          authId,
          workspaceId,
          chatId,
          latestValuesRef.current,
        );
      }
    };
  }, [authId, chatId, workspaceId]);
}

type tSendState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'rejected'; message: string }
  | { kind: 'sent' };

type tUseInteractiveComposerArgs = {
  /**
   * Optional preload — e.g., populated from a selected interactive quick
   * reply. Must be a deep copy (see QuickAction.component.tsx `structuredClone`
   * pattern) so mutation here does not leak into the quick reply list.
   */
  preload?: tInteractiveOutbound;
};

export function useInteractiveComposer(args: tUseInteractiveComposerArgs = {}) {
  const { selectedChat } = useChatContext();
  const {
    setSelectedAction,
    interactiveDraft,
    setInteractiveDraft,
    setMessageBarValue,
    draftMessages,
    setDraftMessages,
  } = useMessageBarContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUser } = useUserContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { canEditInteractiveFooter, userIsOwner, userIsAdmin } =
    useACLContext();
  const canTopUpBalance = userIsOwner || userIsAdmin;
  const { addAlert } = useToastContext();
  const { trackWapiEvent } = useWapiAnalytics();
  const { blockWapiPlanSend } = useWapiPlanSendGuard();
  // Only for the copy of a send the server refused.
  const { t } = useTranslation('errors/errors');

  const [sendState, setSendState] = useState<tSendState>({ kind: 'idle' });
  const [fieldErrors, setFieldErrors] = useState<
    tWapiInteractiveErrorBody['errors']
  >([]);

  // Meta's API expects the recipient phone number as `to` — NOT the lead
  // UUID. The ChatModal context exposes the current lead (with `handle`
  // = phone), matching the pattern used by template / reaction sends.
  const handle = selectedLeadForChatModal?.handle ?? '';
  const chatId = selectedChat?.id ?? '';
  const authId = currentUser?.authId ?? '';

  // Preload precedence: explicit prop > context draft (from quick reply
  // select) > persisted localStorage cache (from a prior close / type
  // switch on this chat) > empty new composer. Context draft is consumed
  // once and cleared so reopening the composer shows a fresh form when no
  // cache exists. Intentionally stable across renders — preload is only
  // consumed on mount.
  const initialValues = useMemo(() => {
    const cached =
      authId && currentWorkspaceId && chatId
        ? InteractiveDraftCache.load(authId, currentWorkspaceId, chatId)
        : undefined;
    return buildInitialInteractiveValues(
      args.preload ?? interactiveDraft ?? cached,
    );
  }, []);

  // Clear the context draft once mounted so a subsequent open of the composer
  // starts fresh unless a new quick reply is selected.
  useEffect(() => {
    if (interactiveDraft) {
      setInteractiveDraft(undefined);
    }
  }, []);

  // If the chat closes (window expired / lead becomes inactive) while the
  // composer is open, bounce back to CHAT so the seller isn't stuck editing.
  useEffect(() => {
    if (!selectedChat?.leadId) {
      setSelectedAction(ChatEnum.CHAT);
    }
  }, [selectedChat?.leadId, setSelectedAction]);

  const handleClose = useCallback(() => {
    setSelectedAction(ChatEnum.CHAT);
  }, [setSelectedAction]);

  const handleSend = useCallback(
    async (values: tInteractiveOutbound) => {
      if (!currentWorkspaceId || !handle) {
        addAlert({
          type: eAlertType.ERROR,
          message: 'No workspace or recipient available',
        });
        return;
      }
      // RET-2349: an interactive message always goes out by WhatsApp API.
      if (blockWapiPlanSend(eChannelType.WHATSAPP_API)) return;

      trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_SEND_ATTEMPTED, {
        conversationId: selectedChat?.id ?? '',
        leadId: selectedChat?.leadId ?? '',
        type: values.type,
      });

      setSendState({ kind: 'sending' });
      setFieldErrors([]);
      try {
        const payload = toMetaWireInteractive(values, {
          canEditFooter: canEditInteractiveFooter,
        });
        // WBS's `to` validator for the interactive endpoint uses the
        // `e164` tag, which requires a leading `+`. Lead handles on the
        // frontend (selectedLeadForChatModal.handle) are stored without
        // the prefix — prepend it here so the wire shape passes.
        // Idempotent: if a caller already supplies a `+`, we don't add a
        // second one.
        const toE164 = handle.startsWith('+') ? handle : `+${handle}`;
        // INT-1538 U10: a person sending from the chat; recorded first.
        await withHumanAction(currentWorkspaceId, selectedChat, () =>
          ChatService.sendWapiInteractive(currentWorkspaceId, {
            to: toE164,
            interactive: payload,
          }),
        );
        // bff-web's SendInteractiveResponse is {code, message, providerId}
        // (no `success` field). Axios resolves for any 2xx, so reaching this
        // point means WBS accepted the send — failures throw and land in
        // the catch block below.
        setSendState({ kind: 'sent' });
        trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_SEND_SUCCEEDED, {
          type: values.type,
          conversationId: selectedChat?.id ?? '',
          leadId: selectedChat?.leadId ?? '',
          author_id: currentUser?.authId ?? '',
        });
        // Clear the shared text input so the next chat visit doesn't see the
        // stale draft still in the composer body.
        setMessageBarValue('');
        // Also evict the per-lead entry from the persisted `draftMessages`
        // localStorage store. Without this, `setMessageBarValue('')` clears
        // in-memory state but the MessageBar mount effect rehydrates from
        // `draftMessages[leadId].message` the next time the seller opens this
        // chat — so the body text of the just-sent interactive reappears as
        // a stale draft. Mirrors the exact cleanup the plain text-send path
        // does in TextAreaContainer after a successful send.
        const leadIdForDraft = selectedChat?.leadId;
        if (leadIdForDraft && Array.isArray(draftMessages)) {
          const idx = draftMessages.findIndex(
            (d) => d.leadId === leadIdForDraft,
          );
          if (idx > -1) {
            const next = [...draftMessages];
            next.splice(idx, 1);
            setDraftMessages(next);
          }
        }
        // Draft successfully sent — drop the cached version so the next
        // composer open starts fresh. Failures intentionally keep the cache
        // so the seller can retry without losing their work.
        if (authId && currentWorkspaceId && chatId) {
          InteractiveDraftCache.clear(authId, currentWorkspaceId, chatId);
        }
        setSelectedAction(ChatEnum.CHAT);
      } catch (err) {
        // bff-web surfaces structured VALIDATION_ERROR as HTTP 400 with the
        // tWapiInteractiveErrorBody shape. Extract and display field-level
        // errors if present.
        const errBody =
          (err as { response?: { data?: tWapiInteractiveErrorBody } }).response
            ?.data ?? undefined;
        if (errBody?.errors?.length) {
          setFieldErrors(errBody.errors);
          setSendState({
            kind: 'rejected',
            message: errBody.message ?? 'Validation failed',
          });
          trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_SEND_REJECTED, {
            type: values.type,
            reason: errBody.code ?? 'VALIDATION_ERROR',
          });
          return;
        }

        // A send the outbound gate refused (423: the plan is paused, or the
        // send could not be tied to its workspace) carries no field errors,
        // so it used to land here as axios's "Request failed with status
        // code 423".
        const refusal = lookupOutboundRefusal(err);
        const refusalAlert = refusal
          ? buildOutboundRefusalAlert(refusal, t)
          : undefined;
        // RET-2376: a send the wallet blocked reads like the template send.
        const walletAlert = resolveSendErrorAlert(
          err,
          t,
          canTopUpBalance,
          'wallet',
        );
        const shownAlert = walletAlert ?? refusalAlert;
        const fallback =
          shownAlert?.message ??
          (err as Error)?.message ??
          'Failed to send interactive message';
        setSendState({ kind: 'rejected', message: fallback });
        addAlert(shownAlert ?? { type: eAlertType.ERROR, message: fallback });
        trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_SEND_REJECTED, {
          type: values.type,
          reason: 'NETWORK_ERROR',
        });
      } finally {
        // `sendState` is the source of truth for in-flight status; the
        // catch/return paths above transition it to rejected/sent.
      }
    },
    [
      addAlert,
      authId,
      blockWapiPlanSend,
      canEditInteractiveFooter,
      canTopUpBalance,
      chatId,
      currentUser?.authId,
      currentWorkspaceId,
      draftMessages,
      handle,
      selectedChat?.id,
      selectedChat?.leadId,
      setDraftMessages,
      setMessageBarValue,
      setSelectedAction,
      t,
      trackWapiEvent,
    ],
  );

  const isSending = sendState.kind === 'sending';

  return {
    authId,
    chatId,
    currentWorkspaceId: currentWorkspaceId ?? '',
    initialValues,
    sendState,
    isSending,
    fieldErrors,
    handleClose,
    handleSend,
  };
}
